export const closeAllTooltips = () => {
  const selectors = [
    '.rich-text-tooltip-input-container',
    '.custom-tooltip-edit-box',
    '.custom-tooltip-hover-display',
  ];
  selectors.forEach((selector) => {
    const elements = document.body.querySelectorAll(selector);
    elements.forEach((element) => {
      if (!element.isConnected) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA') &&
        element.contains(activeElement)
      ) {
        // Tooltip is being edited, don't close it
        return;
      }

      const parent = element.parentNode;
      if (!parent) return;

      if (!parent.contains(element)) return;

      try {
        parent.removeChild(element);
      } catch (error) {
        // Already removed
      }
    });
  });
};

export const isInsideSummaryContainer = (
  elementOrQuill: HTMLElement | { quill: any; index: number; length: number },
): boolean => {
  let element: HTMLElement | null = null;

  if ('quill' in elementOrQuill) {
    const { quill } = elementOrQuill;
    element = quill.container;
  } else {
    element = elementOrQuill;
  }

  if (!element) return false;

  let current: HTMLElement | null = element;

  while (current) {
    if (
      current.classList &&
      (current.classList.contains('rich-text-editor-summary') ||
        current.classList.contains('rich-text-summary'))
    ) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};

export const getTargetRectViewportForElement = (
  element: HTMLElement,
): {
  top: number;
  left: number;
  width: number;
  height: number;
} => {
  const rect = element.getBoundingClientRect();
  if (isMultilineElement(element)) {
    const firstWordRect = getFirstWordRectForElement(element);
    if (firstWordRect) {
      return firstWordRect;
    }
  }
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

export const removeExistingTooltips = () => {
  const existing = document.body.querySelectorAll(
    '.rich-text-tooltip-input-container, .custom-tooltip-edit-box, .custom-tooltip-hover-display',
  );

  existing.forEach((element) => {
    if (!element.isConnected) return;

    const parent = element.parentNode;
    if (!parent) return;

    if (!parent.contains(element)) return;

    try {
      parent.removeChild(element);
    } catch (error) {
      // Already removed
    }
  });
};

export const createTooltipContainer = (
  className: string,
  isInSummary: boolean,
): HTMLElement => {
  const container = document.createElement('div');
  container.className = `${className} rich-text-tooltip-container${
    isInSummary ? ' white' : ''
  }`;
  return container;
};

export const addCaretToTooltip = (container: HTMLElement) => {
  const caret = document.createElement('div');
  caret.className = 'rich-text-tooltip-caret';
  container.appendChild(caret);
};

export const positionAndStyleTooltip = (
  tooltipBox: HTMLElement,
  targetRectViewport: {
    top: number;
    left: number;
    width: number;
    height: number;
  },
) => {
  const { top, left } = positionTooltip(tooltipBox, targetRectViewport);
  tooltipBox.style.left = `${left}px`;
  tooltipBox.style.top = `${top}px`;
  tooltipBox.style.transform = 'translateX(-50%)';
};

export const positionTooltip = (
  tooltipBox: HTMLElement,
  targetRectViewport: {
    top: number;
    left: number;
    width: number;
    height: number;
  },
  gap = 8,
): { top: number; left: number } => {
  const wasAppended = tooltipBox.parentElement === document.body;
  if (!wasAppended) {
    tooltipBox.style.visibility = 'hidden';
    document.body.appendChild(tooltipBox);
  }

  const tooltipHeight = tooltipBox.offsetHeight;
  const targetTop = targetRectViewport.top;

  const actualGap = Math.max(gap, 12);
  const top = targetTop - tooltipHeight - actualGap + 6;
  const left = targetRectViewport.left + targetRectViewport.width / 2;

  if (!wasAppended) {
    const parent = tooltipBox.parentNode;
    if (parent && parent.contains(tooltipBox)) {
      try {
        parent.removeChild(tooltipBox);
      } catch (error) {
        // Already removed
      }
    }
    tooltipBox.style.visibility = '';
  }

  return { top, left };
};

export const isMultilineElement = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const tempSpan = document.createElement('span');
  tempSpan.textContent = 'M';
  tempSpan.style.visibility = 'hidden';
  tempSpan.style.position = 'absolute';
  tempSpan.style.fontSize = window.getComputedStyle(element).fontSize;
  tempSpan.style.fontFamily = window.getComputedStyle(element).fontFamily;
  tempSpan.style.lineHeight = window.getComputedStyle(element).lineHeight;
  document.body.appendChild(tempSpan);

  const singleLineHeight = tempSpan.getBoundingClientRect().height;
  document.body.removeChild(tempSpan);

  return rect.height > singleLineHeight * 1.5;
};

export const getFirstWordRectForElement = (
  element: HTMLElement,
): { top: number; left: number; width: number; height: number } | null => {
  const text = element.textContent || '';
  const words = text.trim().split(/\s+/);
  if (words.length === 0 || words[0].length === 0) {
    return null;
  }

  const firstWord = words[0];
  const firstWordIndex = text.indexOf(firstWord);

  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let charCount = 0;
  let textNode: Text | null = null;
  let startOffset = 0;
  let endOffset = 0;

  while ((textNode = walker.nextNode() as Text | null)) {
    const nodeText = textNode.textContent || '';
    const nodeLength = nodeText.length;

    if (charCount + nodeLength > firstWordIndex) {
      startOffset = firstWordIndex - charCount;
      endOffset = Math.min(startOffset + firstWord.length, nodeLength);
      range.setStart(textNode, startOffset);
      range.setEnd(textNode, endOffset);
      break;
    }

    charCount += nodeLength;
  }

  if (!range.collapsed) {
    const rect = range.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  }

  return null;
};
